import { useTranslation } from 'react-i18next';
import { ContentSectionFullHeight } from '@client/components/connect-container';
import { useRive, useStateMachineInput } from '@rive-app/react-canvas-lite';
import { Organization, Provider } from '@schema/schemas';
import { useState, useEffect, useMemo } from 'react';
import { ENABLE_GUEST_ACCESS_FEATURE } from '@client/config/constants';
import { LoadingAnimationStyle, Copy, AnimationStack } from './verifying-credentials.style';

export type Props = {
  status?: string;
  organization: Organization;
  provider: Provider;
  complete: boolean;
};

export const VerifyingCredentials = ({
  provider,
  complete,
  organization,
  status,
}: Props): JSX.Element => {
  const { t: verifyingCopy } = useTranslation('connect', { keyPrefix: 'components.verifiying' });
  const { t: conclusionCopy } = useTranslation('connect', { keyPrefix: 'components.conclusion' });
  const orgName = organization.displayName || organization.name;
  const shouldShowGuestAccessCopy = !!(
    ENABLE_GUEST_ACCESS_FEATURE &&
    organization.isAutomatedGuestAccessConfigurationEnabled &&
    provider.multiFactorAuthentication.isGuestAccessSupported
  );

  const { rive, RiveComponent } = useRive({
    src: '/assets/animations/connect-loader-universal.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
  });

  const verifyingMessages = useMemo(
    () => [
      verifyingCopy('verifiyingText1'),
      verifyingCopy('verifiyingText2'),
      verifyingCopy('verifiyingText3'),
      verifyingCopy('verifiyingText4'),
    ],
    [verifyingCopy]
  );
  const verifyingSteps = verifyingMessages.length - 1;

  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (complete || messageIndex === verifyingSteps) return;

    const interval = setInterval(() => {
      setMessageIndex(messageIndex + 1);
    }, 15000);

    return () => clearInterval(interval);
  }, [complete, messageIndex, verifyingSteps]);

  // Access the 'isLoaded' input
  const isLoadedInput = useStateMachineInput(rive, 'State Machine 1', 'isLoaded');

  // Set the 'isLoaded' input to true when complete prop is set to true
  if (complete && isLoadedInput) {
    isLoadedInput.value = true;
  }

  const getConclusionText = (): string => {
    if (provider.isThirdPartyPortalSupported) return conclusionCopy('thirdPartyPortal');
    if (status === 'LOGIN_SUCCESS') {
      return shouldShowGuestAccessCopy
        ? conclusionCopy('loginSuccessGuestAccess', { providerName: provider.name })
        : conclusionCopy('loginSuccess', { providerName: provider.name });
    }
    return conclusionCopy('loginTimedOut', { providerName: provider.name, orgName });
  };

  const copy = complete ? getConclusionText() : verifyingMessages[messageIndex];

  return (
    <ContentSectionFullHeight
      centerContentVertically
      centerContentHorizontally
      data-testid="verifying-credentials"
    >
      <AnimationStack>
        <RiveComponent style={LoadingAnimationStyle} data-testid="rive-animation" />
        <Copy>{copy}</Copy>
      </AnimationStack>
    </ContentSectionFullHeight>
  );
};
